import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiasApi from 'config/api/dpias/dpias';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';

import type { OwnerDpiaFormValues } from '../OwnerDpiaEditorPage/ownerDpiaEditor.helpers';

import {
  mapDetailToReviewFormValues,
  validateApproveDecision,
  validateClarificationDecision,
  type AdminDpiaReviewValidationMessages,
} from './adminDpiaReview.helpers';

type Params = {
  dpiaId: number;
  onApproveSuccess?: () => void;
  onClarificationSuccess?: () => void;
  organizationId?: number;
};

const useAdminDpiaReview = ({ dpiaId, onApproveSuccess, onClarificationSuccess, organizationId }: Params) => {
  const queryClient = useQueryClient();
  const [reviewComment, setReviewComment] = useState('');
  const [validationErrors, setValidationErrors] = useState<Partial<Record<'reviewComment' | 'approval', string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.DPIA_REVIEW_DETAIL, organizationId, dpiaId],
    async () => {
      const { data } = await dpiasApi.getReviewDetail(organizationId as number, dpiaId);
      return data;
    },
    {
      enabled: Boolean(organizationId && dpiaId),
      retry: false,
    },
  );

  const formValues = useMemo<OwnerDpiaFormValues | null>(() => {
    if (!detailQuery.data?.dpia) return null;
    return mapDetailToReviewFormValues(detailQuery.data.dpia);
  }, [detailQuery.data?.dpia]);

  useEffect(() => {
    if (detailQuery.data?.dpia) {
      setReviewComment(detailQuery.data.dpia.reviewDecisionComment);
      setValidationErrors({});
    }
  }, [detailQuery.data?.dpia]);

  const approveMutation = useMutation(
    MUTATION_KEYS.DPIA_APPROVE,
    async () => {
      if (!detailQuery.data?.dpia) return null;

      const { data } = await dpiasApi.approve(organizationId as number, dpiaId, {
        submittedRevision: detailQuery.data.dpia.submittedRevision as number,
        reviewDecisionComment: reviewComment,
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_REVIEW_DETAIL, organizationId, dpiaId], data);
        onApproveSuccess?.();
      },
    },
  );

  const clarificationMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_CLARIFICATION,
    async () => {
      if (!detailQuery.data?.dpia) return null;

      const { data } = await dpiasApi.requestClarification(organizationId as number, dpiaId, {
        submittedRevision: detailQuery.data.dpia.submittedRevision as number,
        administratorClarificationComment: reviewComment,
      });

      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_REVIEW_DETAIL, organizationId, dpiaId], data);
        onClarificationSuccess?.();
      },
    },
  );

  const handleApprove = useCallback(
    async (messages: AdminDpiaReviewValidationMessages) => {
      if (!detailQuery.data?.dpia || !formValues) return;

      const errors = validateApproveDecision(detailQuery.data.dpia, formValues, reviewComment, messages);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
        throw new Error('Validation failed');
      }

      await approveMutation.mutateAsync();
    },
    [approveMutation, detailQuery.data?.dpia, formValues, reviewComment],
  );

  const handleRequestClarification = useCallback(
    async (messages: Pick<AdminDpiaReviewValidationMessages, 'clarificationComment'>) => {
      const errors = validateClarificationDecision(reviewComment, messages);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
        throw new Error('Validation failed');
      }

      await clarificationMutation.mutateAsync();
    },
    [clarificationMutation, reviewComment],
  );

  return {
    approveMutation,
    clarificationMutation,
    context: detailQuery.data?.context,
    detail: detailQuery.data?.dpia,
    formValues,
    handleApprove,
    handleRequestClarification,
    isError: detailQuery.isError,
    isLoading: detailQuery.isLoading,
    isApproving: approveMutation.isLoading,
    isRequestingClarification: clarificationMutation.isLoading,
    refetch: detailQuery.refetch,
    reviewComment,
    setReviewComment,
    setValidationErrors,
    validationErrors,
  };
};

export default useAdminDpiaReview;
